import { Dispatch } from 'react-redux';

import { StoreState } from '../../reducers';
import { Action } from '../../actions';
import { Api } from '../../typings/api';
import { DriveItemInfo } from '../DriveItemList/reducer';
import { CallbackAction, getOrFetchDriveItemMeta, runCallbackAction, updateDriveItem } from '../DriveItemList/actions';
import UserAgentUtils from '../../utils/userAgent';
import ApiUtils from '../../utils/api';
import { addPendingShare } from '../PendingTasksManager/actions';
import { createNotification } from '../NotificationBar/actions';
import UrlUtils from '../../utils/url';
import Utils from '../../utils';
import StatsUtils from '../../utils/statistics';
import NativeApi from '../../utils/nativeApi';
import RoutesUtils from '../../utils/routes';
import ValidationUtils from '../../utils/validation';
import { AppType } from '../App/reducer';
import DriveItemUtils from '../../utils/driveItem';
import { FormField } from '../../components/Share/UprezForm/typings';
import IndexedDBManager from '../../utils/indexedDB/manager';
import { SelectionType, toggleSelection } from '../Selection/actions';
import { SelectedNote } from './reducer';
import { CopyMode, ShareViaCopyResource } from './reducer';
import { PopupTheme } from '../../components/Popup';

enum ShareOrigin {
	Detail = '@@ShareOrigin/detail',
	Theater = '@@ShareOrigin/theater',
	Selection = '@@ShareOrigin/selection',
}

enum SharePanelActionKeys {
	OPEN_PANEL = '@@sharePanel/OPEN_PANEL',
	CLOSE_PANEL = '@@sharePanel/CLOSE_PANEL',
	OPEN_SHARE_ACTIONS_POPUP = '@@sharePanel/OPEN_SHARE_ACTIONS_POPUP',
	CLOSE_SHARE_ACTIONS_POPUP = '@@sharePanel/CLOSE_SHARE_ACTIONS_POPUP',
	OPEN_SHARE_VIA_COPY_POPUP = '@@sharePanel/OPEN_SHARE_VIA_COPY_POPUP',
	CLOSE_SHARE_VIA_COPY_POPUP = '@@sharePanel/CLOSE_SHARE_VIA_COPY_POPUP',
	SET_COPY_MODE = '@@sharePanel/SET_COPY_MODE',
	START_PROCESSING = '@@sharePanel/START_PROCESSING',
	FINISH_PROCESSING = '@@sharePanel/FINISH_PROCESSING',
	SET_SELECTION = '@@sharePanel/SET_SELECTION',
	SET_METHOD = '@@sharePanel/SET_METHOD',
	SET_FORM_FIELD = '@@sharePanel/SET_FORM_FIELD',
	RESET_FORM = '@@sharePanel/RESET_FORM',
	UPDATE_FOLDERS_CONTENT = '@@sharePanel/UPDATE_FOLDERS_CONTENT',
	SET_CRM_CONTACTS = '@@sharePanel/SET_CRM_CONTACTS'
}

interface OpenPanelAction extends Action {
	type: SharePanelActionKeys.OPEN_PANEL;
	isPublicationShare: boolean;
	callbackAction?: CallbackAction;
}

interface OpenShareActionsPopupAction extends Action {
	type: SharePanelActionKeys.OPEN_SHARE_ACTIONS_POPUP;
	sharedItems: Api.DriveItem[];
	isPublicationShare: boolean;
	callbackAction: CallbackAction | undefined;
}

interface OpenShareViaCopyPopupAction extends Action {
	type: SharePanelActionKeys.OPEN_SHARE_VIA_COPY_POPUP;
	shareViaCopyData: ShareViaCopyResource[];
	pin: string;
	theme?: PopupTheme;
	callbackAction: CallbackAction | undefined;
}

interface StartProcessingAction extends Action {
	type: SharePanelActionKeys.START_PROCESSING;
}

interface FinishProcessingAction extends Action {
	type: SharePanelActionKeys.FINISH_PROCESSING;
}

interface ClosePanelAction extends Action {
	type: SharePanelActionKeys.CLOSE_PANEL;
}

interface CloseShareActionsPopupAction extends Action {
	type: SharePanelActionKeys.CLOSE_SHARE_ACTIONS_POPUP;
	shouldResetSelection?: boolean;
}

interface CloseShareViaCopyPopupAction extends Action {
	type: SharePanelActionKeys.CLOSE_SHARE_VIA_COPY_POPUP;
}

interface SetCopyMode extends Action {
	type: SharePanelActionKeys.SET_COPY_MODE;
	copyMode: CopyMode;
}

interface ResetFormAction extends Action {
	type: SharePanelActionKeys.RESET_FORM;
}

interface SetSelectionAction extends Action {
	type: SharePanelActionKeys.SET_SELECTION;
	selectedResources?: Api.DriveItem[];
	selectedNotes?: SelectedNote[];
}

interface SetFormFieldAction extends Action {
	type: SharePanelActionKeys.SET_FORM_FIELD;
	name: string;
	value: string;
}

interface UpdateFoldersContentAction extends Action {
	type: SharePanelActionKeys.UPDATE_FOLDERS_CONTENT;
	item: Api.DriveItem;
}

interface SetCrmContactsAction extends Action {
	type: SharePanelActionKeys.SET_CRM_CONTACTS;
	crmContacts: Api.CrmContact[];
}

type SharePanelAction = OpenPanelAction
	| ClosePanelAction
	| OpenShareActionsPopupAction
	| CloseShareActionsPopupAction
	| OpenShareViaCopyPopupAction
	| CloseShareViaCopyPopupAction
	| SetCopyMode
	| StartProcessingAction
	| FinishProcessingAction
	| ResetFormAction
	| SetSelectionAction
	| SetFormFieldAction
	| UpdateFoldersContentAction
	| SetCrmContactsAction;

const share = (
	sharedItems: Api.DriveItem[],
	sharedNotesIds?: number[],
	isPublicationShare = false,
	callbackAction?: CallbackAction,
) => async (dispatch: Dispatch<StoreState>, getState: () => StoreState): Promise<void> => {
	const { appInfos, galleryInfos, authentication } = getState();
	const { needCredentials } = authentication.userInfos;
	const { config: galleryConfig, template: { sharedNetworks } } = galleryInfos;
	const type = appInfos.type || AppType.Single;
	const isNativeShare = UserAgentUtils.isMobileApp && !Utils.isSecure(type, needCredentials);

	StatsUtils.trackShare(sharedItems);

	if (isNativeShare) {
		const title = DriveItemUtils.getShareTitle(sharedItems);
		const url = DriveItemUtils.getShareLink(sharedItems, galleryConfig, ShareOrigin.Selection);
		NativeApi.shareApp(title, url);
		dispatch(runCallbackAction(callbackAction));
	} else {
		if (sharedNotesIds) {
			await dispatch(setSelectedNotesData(sharedNotesIds));
		}

		if (Utils.isMultipleShareEnabled(sharedNetworks)) {
			dispatch(openShareActionsPopup(sharedItems, isPublicationShare, callbackAction));
		} else if (Utils.isShareOnlyViaUprezEnabled(sharedNetworks)) {
			await dispatch(initiateShare(sharedItems, isPublicationShare, Api.ShareNetwork.Uprez, callbackAction));
		} else if (Utils.isShareOnlyViaCopyEnabled(sharedNetworks)) {
			if (isPublicationShare) {
				const { id, label, format } = sharedItems[0];
				await dispatch(sharePublicationViaCopy(galleryConfig.clientId, id, label, format));
			} else {
				await dispatch(initiateShare(sharedItems, false, Api.ShareNetwork.Copy, callbackAction));
			}
		}
	}
};

const setSelectedNotesData = (notesIds: number[]) => async (dispatch: Dispatch<StoreState>): Promise<void> => {
	const selectedNotes: SelectedNote[] = [];

	for (let i = 0; i < notesIds.length; i++) {
		await IndexedDBManager.getNote(notesIds[i]).then((note) => {
			if (note) {
				const { id, title } = note;
				selectedNotes.push({ id, title });
			}
		});
	}

	dispatch({
		type: SharePanelActionKeys.SET_SELECTION,
		selectedNotes,
	});
};

const initiateShare = (
	sharedItems: Api.DriveItem[],
	isPublicationShare: boolean,
	shareAction: Api.ShareNetwork,
	callbackAction?: CallbackAction,
) => async (dispatch: Dispatch<StoreState>, getState: () => StoreState): Promise<void> => {
	const { galleryInfos: { template: { sharedNetworks } } } = getState();

	dispatch({
		type: SharePanelActionKeys.SET_SELECTION,
		selectedResources: sharedItems,
	});

	if (Utils.isShareOnlyViaCopyEnabled(sharedNetworks)) {
		// to display a loader while resources are being retrieved
		dispatch(openShareViaCopyPopup([], '', undefined, callbackAction));
	}

	if (shareAction === Api.ShareNetwork.Uprez) {
		const action: OpenPanelAction = {
			type: SharePanelActionKeys.OPEN_PANEL,
			isPublicationShare,
			callbackAction,
		};
		dispatch(action);
	}

	dispatch({ type: SharePanelActionKeys.START_PROCESSING });

	for (let i = 0; i < sharedItems.length; i++) {
		const item = sharedItems[i];
		if (item.type === Api.DriveItemType.Directory) {
			await dispatch(retrieveSubFoldersContent(item.id));
		}
	}

	dispatch({ type: SharePanelActionKeys.FINISH_PROCESSING });

	if (shareAction === Api.ShareNetwork.Copy) {
		await dispatch(shareItems(true));
	}
};

const finishShare = () => (dispatch: Dispatch<StoreState>) => {
	dispatch({ type: SharePanelActionKeys.RESET_FORM });
	dispatch({ type: SharePanelActionKeys.CLOSE_PANEL });
	dispatch({ type: SharePanelActionKeys.CLOSE_SHARE_ACTIONS_POPUP });
	dispatch({ type: SharePanelActionKeys.FINISH_PROCESSING });
};

const getContactInfo = (isMultiApp: boolean, hasMultipleContacts: boolean) =>
	(dispatch: Dispatch<StoreState>, getState: () => StoreState): Api.SharedContactInfo | {} | undefined => {
	const { form } = getState().sharePanel;
	const { contactEmail, company, firstname, phone, position, surname } = form;
	if (!isMultiApp) {
		return undefined;
	} else if (hasMultipleContacts) {
		return {};
	} else {
		return {
			company,
			forename: firstname,
			email: contactEmail,
			name: surname,
			phone,
			position
		};
	}
};

const shareItems = (isShareViaCopy?: boolean) => async (dispatch: Dispatch<StoreState>, getState: () => StoreState) => {
	const {
		appInfos,
		sharePanel,
		i18n: { locale },
		driveList: { driveItems },
	} = getState();
	const { form, isPublicationShare, selectedResources, callbackAction } = sharePanel;
	const { userName, userEmail, contactEmail, message, subject, createDeal } = form;
	const isMultiApp = Utils.isMultiApp(appInfos);
	const resources = dispatch(retrieveResources());
	const { notesToShare, selectedNotesIds } = await dispatch(retrieveNotes());

	// Sending contactInfo is how backend differenciate between SingleApp and MultiApp share
	// In SingleApp it needs to be undefined (subject is also inexistant in SingleApp)
	// In MultiApp, if there is only one recipient the only property that is required in "contactInfo" is "email"
	// If there is multiple recipients, contactInfo must be an empty object
	// In both cases, "toAddresses" is the only required field by the API

	const emailsArray = contactEmail
		.split(/[ ,]+/)
		.filter(item => item !== '');
	const hasInvalidEmail = emailsArray.some(item => !ValidationUtils.isValidEmail(item));

	if (hasInvalidEmail) {
		dispatch(createNotification('error.invalid_email_address'));
		return;
	}

	const contactInfo = dispatch(getContactInfo(isMultiApp, emailsArray.length > 1));

	const requestData: Api.SharedData = {
		fromName: !isMultiApp ? userName : '',
		fromEmail: !isMultiApp ? userEmail : '',
		toAddresses: emailsArray,
		message,
		language: locale,
		resources,
		contactInfo,
		subject: isMultiApp ? subject : undefined,
		createDeal,
		forCopy: isShareViaCopy,
		notes: notesToShare,
	};

	const isOnline = await UserAgentUtils.isOnlinePromise();

	if (isOnline) {
		dispatch({ type: SharePanelActionKeys.START_PROCESSING });
		try {
			const response = await ApiUtils.shareContent(requestData);
			if (response === 'response=ko') {
				// If an error occured, the server still return a success
				// But the response is KO
				throw(new Error('share failed'));
			}

			dispatch(finishShare());

			if (isShareViaCopy) {
				const { resources, notes, pin } = JSON.parse(response);
				dispatch(shareViaCopy(resources, notes, pin, callbackAction));
			}

			if (isPublicationShare) {
				dispatch(finishPublicationSharing(resources[0].id, selectedResources[0].format));
			} else {
				// change status of shared proposals
				const draftProposalsIds = resources
					.map(({ id }) => id)
					.filter(id => DriveItemUtils.isDraftProposal(driveItems[id]));

				for (let i = 0; i < draftProposalsIds.length; i++) {
					ApiUtils.getMeta(draftProposalsIds[i])
						.then(item => {
							dispatch(updateDriveItem(item));
						});
				}
			}

			if (selectedNotesIds.length > 0) {
				const { notes: sharedNotes } = JSON.parse(response);
				dispatch(replaceNotes(selectedNotesIds, sharedNotes));
			}

			if (!isShareViaCopy) {
				dispatch(runCallbackAction(callbackAction));
			}
		} catch (error) {
			const errorMessage = isPublicationShare
				? getPublicationShareErrorMessage(selectedResources[0].format)
				: 'error.share_failed_request';
			dispatch(createNotification(errorMessage));
			dispatch({ type: SharePanelActionKeys.FINISH_PROCESSING });
		}
	} else {
		dispatch(addPendingShare(requestData));
		dispatch(createNotification('info.selection_shared_once_online'));
		dispatch(finishShare());
		dispatch(runCallbackAction(callbackAction));
	}
};

const shareViaCopy = (
	resources?: { id: number, url: string }[],
	notes?: { title: string, url: string }[],
	pin?: string,
	callbackAction?: CallbackAction,
) => (dispatch: Dispatch<StoreState>, getState: () => StoreState) => {
	const { driveList: { driveItems } } = getState();
	const resourceList: ShareViaCopyResource[] = [];

	if (resources) {
		resources.forEach(({ id, url }) => {
			const { label } = driveItems[id];
			resourceList.push({ url, label });
		});
	}

	if (notes) {
		notes.forEach(({ title, url }) => {
			resourceList.push({ url, label: title });
		});
	}

	dispatch(openShareViaCopyPopup(resourceList, pin || '', undefined, callbackAction));
};

const retrieveResources = () => (dispatch: Dispatch<StoreState>, getState: () => StoreState) => {
	const { galleryInfos, sharePanel } = getState();
	const galleryConfig = galleryInfos.config;
	const { selectedResources, foldersContent } = sharePanel;
	const sharedContent = [...selectedResources.filter(item => item.type !== Api.DriveItemType.Directory), ...foldersContent];

	return sharedContent.map(item => {
		const { type, id, originalFile, src } = item;
		let url = '';

		switch (type) {
			case Api.DriveItemType.Gallery:
			case Api.DriveItemType.AppContainer:
				url = `${UrlUtils.getConfiguredRootApiClient(galleryConfig, false)}/${id}`;
				break;
			case Api.DriveItemType.Publication:
				url = UrlUtils.getPublicationUrl(galleryConfig, item, 1, false, true);
				break;
			case Api.DriveItemType.ExternalLink:
				const linkId = src.replace('$media$', '');
				url = `${UrlUtils.getConfiguredRootApiClient(galleryConfig, false)}/${linkId}`;
				break;
			default:
				url = `${UrlUtils.getConfiguredRootApiClient(galleryConfig, false)}/${originalFile}`;
				break;
		}

		return { id, url };
	});
};

const retrieveNotes = () => async (dispatch: Dispatch<StoreState>, getState: () => StoreState): Promise<{
	notesToShare: Api.NoteData[],
	selectedNotesIds: number[],
}> => {
	const { sharePanel: { selectedNotes } } = getState();
	const notesToShare: Api.NoteData[] = [];
	const selectedNotesIds: number[] = [];

	for (let i = 0; i < selectedNotes.length; i++) {
		const noteId = selectedNotes[i].id;

		await IndexedDBManager.getNote(noteId).then((note) => {
			if (note) {
				notesToShare.push(note);
				selectedNotesIds.push(noteId);
			}
		});
	}

	return {
		notesToShare,
		selectedNotesIds,
	};
};

const addSubFoldersContent = (item: Api.DriveItem) => (dispatch: Dispatch<StoreState>, getState: () => StoreState) => {
	const { sharePanel } = getState();
	const { selectedResources } = sharePanel;

	if (item.id && !selectedResources.some(parent => parent.id === item.id)) {
		dispatch({
			type: SharePanelActionKeys.UPDATE_FOLDERS_CONTENT,
			item,
		});
	}
};

const setCrmContacts = (crmContacts: Api.CrmContact[]) => (dispatch: Dispatch<StoreState>) => {
	const action: SetCrmContactsAction = {
		type: SharePanelActionKeys.SET_CRM_CONTACTS,
		crmContacts,
	};
	dispatch(action);
};

const contactKeyByFieldName = {
	[FormField.Firstname]: 'firstName',
	[FormField.Surname]: 'lastName',
	[FormField.Email]: 'email',
	[FormField.Company]: 'company',
	[FormField.Position]: 'position',
	[FormField.Phone]: 'phone',
};

const setFormFieldByContactField = (name: FormField, value: string | null) => (dispatch: Dispatch<StoreState>) => {
	const action: SetFormFieldAction = {
		type: SharePanelActionKeys.SET_FORM_FIELD,
		name,
		value: value || '',
	};
	dispatch(action);
};

const setFormFieldsByContact = (crmContact: Api.CrmContact) => (dispatch: Dispatch<StoreState>) => {
	const formKeys = Object.keys(contactKeyByFieldName) as FormField[];
	formKeys.forEach((formKey) => {
		const contactKey = contactKeyByFieldName[formKey];
		const contactField = crmContact[contactKey] as string | null;
		dispatch(setFormFieldByContactField(formKey, contactField));
	});
};

const retrieveSubFoldersContent = (itemId: number) => async (dispatch: Dispatch<StoreState>, getState: () => StoreState) => {
	try {
		const { driveList: { driveItems } } = getState();
		let itemMeta = driveItems[itemId];

		if (!itemMeta || (itemMeta && itemMeta.type === Api.DriveItemType.Directory)) {
			itemMeta = await dispatch(getOrFetchDriveItemMeta(itemId)) as DriveItemInfo;
		}

		const isFolder = itemMeta.type === Api.DriveItemType.Directory;

		if (isFolder) {
			const { resources } = itemMeta;
			if (resources) {
				for (let i = 0; i < resources.length; i++) {
					await dispatch(retrieveSubFoldersContent(resources[i].id));
				}
			}
		} else {
			dispatch(addSubFoldersContent(itemMeta));
		}
	} catch (error) {
		dispatch({ type: SharePanelActionKeys.FINISH_PROCESSING });
	}
};

const openShareActionsPopup = (sharedItems: Api.DriveItem[], isPublicationShare: boolean, callbackAction: CallbackAction | undefined): OpenShareActionsPopupAction => ({
	type: SharePanelActionKeys.OPEN_SHARE_ACTIONS_POPUP,
	sharedItems,
	isPublicationShare,
	callbackAction,
});

const closeShareActionsPopup = (shouldResetSelection?: boolean): CloseShareActionsPopupAction => ({
	type: SharePanelActionKeys.CLOSE_SHARE_ACTIONS_POPUP,
	shouldResetSelection,
});

const openShareViaCopyPopup = (shareViaCopyData: ShareViaCopyResource[], pin: string, theme?: PopupTheme, callbackAction?: CallbackAction): OpenShareViaCopyPopupAction => ({
	type: SharePanelActionKeys.OPEN_SHARE_VIA_COPY_POPUP,
	shareViaCopyData,
	pin,
	theme,
	callbackAction,
});

const closeShareViaCopyPopup = (): CloseShareViaCopyPopupAction => ({
	type: SharePanelActionKeys.CLOSE_SHARE_VIA_COPY_POPUP,
});

const setCopyMode = (copyMode: CopyMode): SetCopyMode => ({
	type: SharePanelActionKeys.SET_COPY_MODE,
	copyMode,
});

const finishPublicationSharing = (resourceId: number, format?: Api.PublicationFormat) => async (dispatch: Dispatch<StoreState>, getState: () => StoreState): Promise<void> => {
	try {
		const resource = await ApiUtils.getMeta(resourceId);
		dispatch(updateDriveItem(resource));

		if (DriveItemUtils.isProposal(format)) {
			const { driveList: { driveItems } } = getState();
			const parentId = DriveItemUtils.getParentFolderId(driveItems[resourceId]);
			RoutesUtils.redirectToParentDir(parentId);
		}
	} catch (error) {
		const errorMessage = getPublicationShareErrorMessage(format);
		dispatch(createNotification(errorMessage));
	}
};

const sharePublicationViaCopy = (
	clientId: number,
	resourceId: number,
	label: string,
	format?: Api.PublicationFormat,
	theme?: PopupTheme,
) => async (dispatch: Dispatch<StoreState>): Promise<void> => {
	try {
		const {
			pin,
			resourceUrl,
		} = await ApiUtils.sharePublication(clientId, resourceId, label);

		dispatch(finishPublicationSharing(resourceId, format));

		const resourceList: ShareViaCopyResource[] = [];
		resourceList.push({ url: resourceUrl, label });
		dispatch(openShareViaCopyPopup(resourceList, pin, theme));
	} catch (error) {
		const errorMessage = getPublicationShareErrorMessage(format);
		dispatch(createNotification(errorMessage));
	}
};

// when note is shared it should be replaced with one from server in selection and removed from indexedDB
const replaceNotes = (
	selectedNotesIds: number[],
	sharedNotes: { id: number, date: number }[]
) => async (dispatch: Dispatch<StoreState>): Promise<void> => {
	for (let i = 0; i < selectedNotesIds.length; i++) {
		const selectedNoteId = selectedNotesIds[i];
		const { id: sharedNoteId, date } = sharedNotes[i];

		const selectedNote = await IndexedDBManager.getNote(selectedNoteId);
		dispatch(toggleSelection(selectedNoteId, SelectionType.NOTES));

		if (selectedNote) {
			await IndexedDBManager.setNote(sharedNoteId, {
				...selectedNote,
				id: sharedNoteId,
				date,
			});
			dispatch(toggleSelection(sharedNoteId, SelectionType.NOTES));
			await IndexedDBManager.removeNote(selectedNoteId);
		}
	}
};

const getPublicationShareErrorMessage = (format?: Api.PublicationFormat) => (
	DriveItemUtils.isProposal(format)
		? 'error.share_proposal_error'
		: 'error.share_publication_error'
);

export {
	SharePanelAction, SharePanelActionKeys, ShareOrigin, shareItems, setFormFieldsByContact, setCrmContacts,
	initiateShare, retrieveSubFoldersContent, share, finishShare, contactKeyByFieldName, closeShareActionsPopup, closeShareViaCopyPopup, setCopyMode, sharePublicationViaCopy
};



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/containers/Share/actions.ts